import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Download, LogOut, Users, RefreshCw, ShoppingBag } from "lucide-react";
import { LogoBadge } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cd_admin_token";

const PREORDER_COLS = [
  ["name", "Name"],
  ["size", "Size"],
  ["quantity", "Qty"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["note", "Note"],
  ["created_at", "Ordered"],
];

const TABS = {
  conference: { label: "Conference Registrations", endpoint: "/admin/conference-registrations", icon: Users, file: "conference-registrations" },
  preorders: { label: "T-Shirt Pre-orders", endpoint: "/admin/preorders", icon: ShoppingBag, file: "tshirt-preorders" },
};

const COLS = [
  ["participant_name", "Name"],
  ["age", "Age"],
  ["institution", "Institution / Workplace"],
  ["in_school", "In school"],
  ["course_of_study", "Course"],
  ["level", "Level"],
  ["qr_method", "QR via"],
  ["qr_contact", "QR contact"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["food_allergies", "Allergies"],
  ["parent_name", "Guardian"],
  ["parent_contact", "Guardian contact"],
  ["join_listen_family", "Listen family"],
  ["volunteer", "Volunteer"],
  ["receive_event_updates", "Updates"],
  ["created_at", "Registered"],
];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-ink/20 py-3.5 font-sans text-lg text-ink placeholder-ink/35 focus:outline-none focus:border-magenta transition-colors";

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-4">Admin</p>
        <h1 className="font-serif font-light text-4xl text-ink">Dashboard access</h1>
        <div className="mt-10 space-y-6">
          <input data-testid="admin-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="rounded-full brand-gradient px-8 py-3.5 font-display text-xs uppercase tracking-[0.14em] font-bold text-white hover:scale-105 transition-transform disabled:opacity-60">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("conference");
  const activeCols = tab === "preorders" ? PREORDER_COLS : COLS;

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.get(`${API}${TABS[tab].endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed, tab]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  const exportCSV = () => {
    const header = activeCols.map(([, label]) => `"${label}"`).join(",");
    const lines = rows.map((r) =>
      activeCols.map(([key]) => {
        let v = r[key];
        if (typeof v === "boolean") v = v ? "Yes" : "No";
        v = v == null ? "" : String(v);
        return `"${v.replace(/"/g, '""')}"`;
      }).join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${TABS[tab].file}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const fmt = (v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : v == null || v === "" ? "—" : String(v));
  const TitleIcon = TABS[tab].icon;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <div className="sticky top-0 z-20 glass-light border-b border-ink/10">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/home"><LogoBadge className="!bg-transparent !px-0 !py-0" /></Link>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-display text-xs uppercase tracking-[0.12em] text-ink/70 hover:border-magenta hover:text-magenta transition-colors">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCSV} data-testid="admin-export" className="inline-flex items-center gap-2 rounded-full brand-gradient px-5 py-2 font-display text-xs uppercase tracking-[0.12em] font-bold text-white hover:scale-105 transition-transform">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-display text-xs uppercase tracking-[0.12em] text-ink/70 hover:border-magenta hover:text-magenta transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
          {Object.entries(TABS).map(([key, t]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 font-display text-xs uppercase tracking-[0.12em] font-bold transition-all ${
                tab === key ? "brand-gradient text-white" : "border border-ink/20 text-ink/60 hover:border-magenta hover:text-magenta"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <TitleIcon size={22} className="text-magenta" />
          <h1 className="font-serif font-light text-3xl md:text-4xl text-ink">{TABS[tab].label}</h1>
          <span className="rounded-full brand-gradient px-3 py-1 font-display text-xs font-bold text-white" data-testid="admin-count">{rows.length}</span>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-ink/10 bg-white card-shadow">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-ink/10 bg-ink/[0.03]">
                {activeCols.map(([key, label]) => (
                  <th key={key} className="whitespace-nowrap px-4 py-3 text-left font-display text-[0.65rem] uppercase tracking-[0.12em] text-ink/50">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={activeCols.length} className="px-4 py-10 text-center text-ink/40 font-sans">Nothing here yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-ink/5 hover:bg-ink/[0.02]" data-testid="admin-row">
                  {activeCols.map(([key]) => (
                    <td key={key} className="whitespace-nowrap px-4 py-3 text-ink/80 max-w-[220px] truncate">
                      {key === "created_at" ? new Date(r[key]).toLocaleDateString() : fmt(r[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
